import Vue from 'vue'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'

@Module({
  namespaced: true
})
export default class CustomFieldsStore extends VuexModule {
  customFields: KwixeoCustomFields.Fields = []
  customFieldsLoaded = false
  layouts: KwixeoCustomFields.Layouts = []
  layoutsLoaded = false

  // CLEAR STORE STATE
  @Action
  clearStore (): void {
    const elements = ['Fields', 'Layouts']
    elements.forEach(key => {
      this.context.commit('set' + key, [])
      this.context.commit('set' + key + 'Loaded', false)
    })
  }

  get getCustomFields (): (usage: string|string[]) => KwixeoCustomFields.Fields {
    return (usage: string|string[]): KwixeoCustomFields.Fields => {
      if (typeof usage === 'string') {
        usage = [usage]
      }

      return this.customFields.filter(field => {
        return field.usage.some(use => usage.includes(use))
      })
    }
  }

  get getCustomField (): (id: number) => KwixeoCustomFields.Field|null {
    return id => this.customFields.find(field => field.id === Number(id)) ??  null
  }

  @Mutation
  setFieldsLoaded (state = true): void {
    this.customFieldsLoaded = state
  }

  @Mutation
  setFields (customFields: KwixeoCustomFields.Fields): void {
    this.customFields = customFields
  }

  @Mutation
  updateField (field: KwixeoCustomFields.Field): void {
    if (!this.customFieldsLoaded) {
      return
    }
    const index = this.customFields.findIndex(val => val.id === field.id)
    if (index >= 0) {
      Vue.set(this.customFields, index, field)
    } else {
      this.customFields.push(field)
    }
  }

  @Mutation
  removeField (field: KwixeoCustomFields.Field): void {
    if (!this.customFieldsLoaded) {
      return
    }
    const index = this.customFields.findIndex(val => val.id === field.id)
    if (index >= 0) {
      Vue.delete(this.customFields, index)
    }
  }

  @Action
  async loadCustomFields (force = false): Promise<void> {
    if (!this.customFieldsLoaded || force) {
      const fields: KwixeoCustomFields.Fields = []

      let page = 1
      let loading = true
      while (loading) {
        const response = await Vue.prototype.$kwCustomFields.axios.get('/config/custom_field', {
          params: {
            per_page: 500,
            page,
            sort: 'order',
            include: 'options'
          }
        })
        fields.push(...response.data.data)
        page++
        loading = page < response.data.meta.last_page
      }
      await this.context.commit('setFields', fields)
      this.context.commit('setFieldsLoaded')
    }
  }

  @Action
  setCustomField (field: KwixeoCustomFields.Field): void {
    this.context.commit('updateField', field)
    if (this.customFieldsLoaded) {
      this.context.commit('orderList')
    }
  }

  @Action
  deleteCustomField (field: KwixeoCustomFields.Field): void {
    this.context.commit('removeField', field)
  }

  get getLayout (): (usage: string|string[]) => KwixeoCustomFields.UsableLayoutContents {
    return (usage: string|string[]): KwixeoCustomFields.UsableLayoutContents => {
      if (typeof usage === 'string') {
        usage = [usage]
      }

      const layout = this.layouts.filter(layout => {
        return layout.usages.some(use => usage.includes(use))
      })[0] ?? null

      if (!layout) {
        return [
          {
            size: 12,
            contents: this.getCustomFields(usage)
          }
        ]
      }

      const used: (number|string)[] = []
      const usableLayout= layout.layout.map(layout => {
        const newLayout = JSON.parse(JSON.stringify(layout))
        newLayout.contents = newLayout.contents.map((fieldId: number) => {
          used.push(fieldId)
          return this.getCustomField(fieldId)
        }).filter((field: KwixeoCustomFields.Field|null) => {
          return field && field.usage.some(use => usage.includes(use))
        })

        return newLayout
      })

      const missing = this.getCustomFields(usage)
        .map(field => String(field.id))
        .filter(fiedId => !used.includes(fiedId))
        .map(id => Number(id));

      if (missing.length > 0) {
        usableLayout.push({
          size: 12,
          contents: missing.map(fieldId => this.getCustomField(fieldId) as KwixeoCustomFields.Field)
        })
      }

      return usableLayout
    }
  }

  @Mutation
  setLayoutsLoaded (state = true): void {
    this.layoutsLoaded = state
  }

  @Mutation
  setLayouts (layouts: KwixeoCustomFields.Layouts): void {
    this.layouts = layouts
  }

  @Mutation
  updateLayout (layout: KwixeoCustomFields.Layout): void {
    if (!this.layoutsLoaded) {
      return
    }
    const index = this.layouts.findIndex(val => val.id === layout.id)
    if (index >= 0) {
      Vue.set(this.layouts, index, layout)
    } else {
      this.layouts.push(layout)
    }
  }

  @Mutation
  removeLayout (layout: KwixeoCustomFields.Layout): void {
    if (!this.layouts) {
      return
    }
    const index = this.layouts.findIndex(val => val.id === layout.id)
    if (index >= 0) {
      Vue.delete(this.layouts, index)
    }
  }

  @Action
  async loadLayouts (force = false): Promise<void> {
    if (!this.layoutsLoaded || force) {
      const layouts: KwixeoCustomFields.Layouts = []

      let page = 1
      let loading = true
      while (loading) {
        const response = await Vue.prototype.$kwCustomFields.axios.get('/config/custom_field/layout', {
          params: {
            per_page: 500,
            page
          }
        })
        layouts.push(...response.data.data)
        page++
        loading = page < response.data.meta.last_page
      }
      await this.context.commit('setLayouts', layouts)
      this.context.commit('setLayoutsLoaded')
    }
  }

  @Action
  setLayout (layout: KwixeoCustomFields.Layout): void {
    this.context.commit('updateLayout', layout)
  }

  @Action
  deleteLayout (layout: KwixeoCustomFields.Layout): void {
    this.context.commit('removeLayout', layout)
  }

  // Sort a list alphabetically
  @Mutation
  orderList (): void {
    this.customFields.sort((a, b) => {
      const aKey = a.order
      const bKey = b.order
      if (aKey < bKey) return -1
      else if (aKey > bKey) return 1
      else return 0
    })
  }
}
